/**
 * Centralized API Endpoints
 * All API endpoints are defined here for easy tracking and management
 */

export const API_ENDPOINTS = {
  // Auth Endpoints
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    LOGOUT: '/auth/logout',
    ME: '/auth/me',
    SEND_OTP: '/auth/send-otp',
    VERIFY_OTP: '/auth/verify-otp',
    TRUECALLER_LOGIN: '/auth/truecaller-login',
    REFRESH_TOKEN: '/auth/refresh-token',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    CHANGE_PASSWORD: '/auth/change-password',
  },

  // Profile Endpoints
  PROFILE: {
    GET_MATCHES: '/profiles/matches',
    GET_MY_PROFILE: '/profiles/me',
    GET_PROFILE: (id: string) => `/profiles/${id}`,
    UPDATE_PROFILE: '/profiles/me',
    UPLOAD_PHOTO: '/profiles/photos',
    DELETE_PHOTO: (id: string) => `/profiles/photos/${id}`,
    SET_PRIMARY_PHOTO: (id: string) => `/profiles/photos/${id}/primary`,
    SEND_INTEREST: (id: string) => `/profiles/${id}/interest`,
    RESPOND_INTEREST: (id: string) => `/profiles/interests/${id}`,
    SHORTLIST: (id: string) => `/profiles/${id}/shortlist`,
    GET_SHORTLISTED: '/profiles/shortlisted',
    REMOVE_SHORTLIST: (id: string) => `/profiles/${id}/shortlist`,
    SKIP_PROFILE: (id: string) => `/profiles/${id}/skip`,
  },

  // Chat Endpoints
  CHAT: {
    GET_CHATS: '/chats',
    CREATE_CHAT: '/chats/create',
    GET_MESSAGES: (id: string) => `/chats/${id}/messages`,
    SEND_MESSAGE: (id: string) => `/chats/${id}/messages`,
    MARK_READ: (id: string) => `/chats/${id}/read`,
    DELETE_MESSAGE: (chatId: string, messageId: string) => `/chats/${chatId}/messages/${messageId}`,
    UPLOAD_FILE: '/chats/upload',
  },

  // Interaction Endpoints
  INTERACTION: {
    GET_INTERESTS: '/interactions/interests',
    GET_SENT_INTERESTS: '/interactions/interests/sent',
    GET_RECEIVED_INTERESTS: '/interactions/interests/received',
    RESPOND_TO_INTEREST: (id: string) => `/interactions/interests/${id}/respond`,
    GET_SHORTLISTED: '/interactions/shortlisted',
    GET_PROFILE_VIEWS: '/interactions/profile-views',
    GET_WHO_VIEWED_ME: '/interactions/who-viewed-me',
  },

  // Plan Endpoints (User Plans - Monthly Subscriptions)
  PLAN: {
    GET_ALL: '/plans',
    GET_PLAN: (id: string) => `/plans/${id}`,
    GET_MY_PLAN: '/plans/my-plan',
    SUBSCRIBE: '/plans/subscribe',
  },

  // Premium Plan Endpoints (Premium Shop - One-time Boosters)
  PREMIUM_PLAN: {
    GET_ALL: '/premium-plans',
    GET_PLAN: (id: string) => `/premium-plans/${id}`,
    PURCHASE: '/premium-plans/purchase',
    GET_MY_PURCHASES: '/premium-plans/my-purchases',
  },

  // Payment Endpoints
  PAYMENT: {
    CREATE_ORDER: '/payments/create-order',
    VERIFY_PAYMENT: '/payments/verify',
    GET_ORDERS: '/payments/orders',
    GET_ORDER: (id: string) => `/payments/orders/${id}`,
  },

  // Coupon Endpoints
  COUPON: {
    GET_ALL: '/coupons',
    GET_MY_COUPONS: '/coupons/my-coupons',
    VALIDATE: '/coupons/validate',
    APPLY: '/coupons/apply',
  },

  // Blog Endpoints
  BLOG: {
    GET_ALL: '/blogs',
    GET_BLOG: (slug: string) => `/blogs/${slug}`,
    CREATE: '/blogs',
    UPDATE: (id: string) => `/blogs/${id}`,
    DELETE: (id: string) => `/blogs/${id}`,
    LIKE: (id: string) => `/blogs/${id}/like`,
    ADD_COMMENT: (id: string) => `/blogs/${id}/comments`,
    GUEST_SUBMIT: '/blogs/guest-submit',
  },

  // Community Endpoints
  COMMUNITY: {
    GET_POSTS: '/community/posts',
    GET_POST: (id: string) => `/community/posts/${id}`,
    CREATE_POST: '/community/posts',
    UPDATE_POST: (id: string) => `/community/posts/${id}`,
    DELETE_POST: (id: string) => `/community/posts/${id}`,
    LIKE_POST: (id: string) => `/community/posts/${id}/like`,
    ADD_COMMENT: (id: string) => `/community/posts/${id}/comments`,
    DELETE_COMMENT: (postId: string, commentId: string) => `/community/posts/${postId}/comments/${commentId}`,
  },

  // Notification Endpoints
  NOTIFICATION: {
    GET_ALL: '/notifications',
    GET_UNREAD_COUNT: '/notifications/unread-count',
    MARK_READ: (id: string) => `/notifications/${id}/read`,
    MARK_ALL_READ: '/notifications/mark-all-read',
    DELETE: (id: string) => `/notifications/${id}`,
    DELETE_ALL: '/notifications/delete-all',
  },

  // Activity Endpoints
  ACTIVITY: {
    GET_ALL: '/activity',
    GET_RECENT: '/activity/recent',
    MARK_VIEWED: (id: string) => `/activity/${id}/viewed`,
  },

  // Marketing Endpoints
  MARKETING: {
    GET_STATS: '/marketing/stats',
    GET_LEADS: '/marketing/leads',
    GET_PAYOUTS: '/marketing/payouts',
    REQUEST_PAYOUT: '/marketing/request-payout',
    UPDATE_ACCOUNT: '/marketing/account-details',
    REQUEST_REACTIVATION: '/marketing/request-reactivation',
  },

  // Referral Endpoints
  REFERRAL: {
    GET_MY_CODE: '/referrals/my-code',
    GENERATE_CODE: '/referrals/generate-code',
    GET_EARNINGS: '/referrals/earnings',
    GET_REFERRALS: '/referrals/my-referrals',
  },

  // Wallet Endpoints
  WALLET: {
    GET_BALANCE: '/wallet/balance',
    GET_TRANSACTIONS: '/wallet/transactions',
    ADD_MONEY: '/wallet/add-money',
    WITHDRAW: '/wallet/withdraw',
  },

  // Settings Endpoints
  SETTINGS: {
    GET_ALL: '/settings',
    UPDATE: '/settings',
    GET_PRIVACY: '/settings/privacy',
    UPDATE_PRIVACY: '/settings/privacy',
    GET_NOTIFICATIONS: '/settings/notifications',
    UPDATE_NOTIFICATIONS: '/settings/notifications',
    GET_CONTACT_VISIBILITY: '/settings/contact-visibility',
    UPDATE_CONTACT_VISIBILITY: '/settings/contact-visibility',
  },

  // FAQ Endpoints
  FAQ: {
    GET_ALL: '/faq',
    GET_BY_CATEGORY: (category: string) => `/faq/category/${category}`,
  },

  // Admin Endpoints
  ADMIN: {
    // Dashboard
    DASHBOARD_STATS: '/admin/dashboard-stats',
    OVERVIEW: '/admin/overview',

    // Users
    GET_USERS: '/admin/users',
    GET_USER: (id: string) => `/admin/users/${id}`,
    UPDATE_USER: (id: string) => `/admin/users/${id}`,
    DELETE_USER: (id: string) => `/admin/users/${id}`,
    BLOCK_USER: (id: string) => `/admin/users/${id}/block`,
    UNBLOCK_USER: (id: string) => `/admin/users/${id}/unblock`,
    UPDATE_USER_STATUS: (id: string) => `/admin/users/${id}/status`,
    APPROVE_USER: (id: string) => `/admin/users/${id}/approve`,
    REJECT_USER: (id: string) => `/admin/users/${id}/reject`,

    // Profile Edit Approvals
    PENDING_PROFILE_CHANGES: '/admin/pending-profile-changes',
    PENDING_PROFILE_CHANGE_DETAILS: (id: string) => `/admin/pending-profile-changes/${id}`,
    APPROVE_PROFILE_CHANGE: (id: string) => `/admin/pending-profile-changes/${id}/approve`,
    REJECT_PROFILE_CHANGE: (id: string) => `/admin/pending-profile-changes/${id}/reject`,

    // Plans (User Plans)
    GET_PLANS: '/admin/plans',
    CREATE_PLAN: '/admin/plans',
    UPDATE_PLAN: (id: string) => `/admin/plans/${id}`,
    DELETE_PLAN: (id: string) => `/admin/plans/${id}`,

    // Premium Plans (Shop Items)
    GET_PREMIUM_PLANS: '/admin/premium-plans',
    CREATE_PREMIUM_PLAN: '/admin/premium-plans',
    UPDATE_PREMIUM_PLAN: (id: string) => `/admin/premium-plans/${id}`,
    DELETE_PREMIUM_PLAN: (id: string) => `/admin/premium-plans/${id}`,

    // Coupons
    GET_COUPONS: '/admin/coupons',
    CREATE_COUPON: '/admin/coupons',
    UPDATE_COUPON: (id: string) => `/admin/coupons/${id}`,
    DELETE_COUPON: (id: string) => `/admin/coupons/${id}`,

    // Payments
    GET_PAYMENTS: '/admin/payments',
    GET_PAYMENT: (id: string) => `/admin/payments/${id}`,
    REFUND_PAYMENT: (id: string) => `/admin/payments/${id}/refund`,

    // Blogs
    GET_BLOGS: '/admin/blogs',
    APPROVE_BLOG: (id: string) => `/admin/blogs/${id}/approve`,
    REJECT_BLOG: (id: string) => `/admin/blogs/${id}/reject`,
    GET_PENDING_BLOGS: '/admin/blogs/pending',

    // Community Posts
    GET_COMMUNITY_POSTS: '/admin/community/posts',
    DELETE_COMMUNITY_POST: (id: string) => `/admin/community/posts/${id}`,
    APPROVE_COMMUNITY_POST: (id: string) => `/admin/community/posts/${id}/approve`,

    // Reported Users
    GET_REPORTED_USERS: '/admin/reported-users',
    UPDATE_REPORT_STATUS: (id: string) => `/admin/reports/${id}`,
    RESOLVE_REPORT: (id: string) => `/admin/reports/${id}/resolve`,

    // Marketing
    GET_MARKETING_USERS: '/admin/marketing/users',
    APPROVE_MARKETING_USER: (id: string) => `/admin/marketing/users/${id}/approve`,
    REJECT_MARKETING_USER: (id: string) => `/admin/marketing/users/${id}/reject`,
    GET_COMMISSIONS: '/admin/marketing/commissions',

    // Payouts
    GET_PAYOUTS: '/admin/payouts',
    APPROVE_PAYOUT: (id: string) => `/admin/payouts/${id}/approve`,
    REJECT_PAYOUT: (id: string) => `/admin/payouts/${id}/reject`,

    // Leads
    GET_LEADS: '/admin/leads',
    ASSIGN_LEAD: '/admin/leads/assign',
    GET_LEAD_ASSIGNMENTS: '/admin/leads/assignments',
    GET_LEAD_SETTINGS: '/admin/leads/settings',
    UPDATE_LEAD_SETTINGS: '/admin/leads/settings',

    // Settings
    GET_SETTINGS: '/admin/settings',
    UPDATE_SETTINGS: '/admin/settings',
    GET_DISCOVERY_SETTINGS: '/admin/settings/discovery',
    UPDATE_DISCOVERY_SETTINGS: '/admin/settings/discovery',
    GET_PROFILE_BOOSTER_SETTINGS: '/admin/settings/profile-booster',
    UPDATE_PROFILE_BOOSTER_SETTINGS: '/admin/settings/profile-booster',
    GET_CONTACT_SETTINGS: '/admin/settings/contact',
    UPDATE_CONTACT_SETTINGS: '/admin/settings/contact',

    // Notifications
    GET_NOTIFICATIONS: '/admin/notifications',
    SEND_NOTIFICATION: '/admin/notifications/send',
    SEND_BULK_NOTIFICATION: '/admin/notifications/send-bulk',

    // Chats
    GET_CHATS: '/admin/chats',
    GET_CHAT_MESSAGES: (id: string) => `/admin/chats/${id}/messages`,
    DELETE_CHAT_MESSAGE: (chatId: string, messageId: string) => `/admin/chats/${chatId}/messages/${messageId}`,

    // Analytics
    GET_ANALYTICS: '/admin/analytics',
    GET_USER_ANALYTICS: '/admin/analytics/users',
    GET_REVENUE_ANALYTICS: '/admin/analytics/revenue',
    GET_ENGAGEMENT_ANALYTICS: '/admin/analytics/engagement',

    // FAQ
    GET_FAQS: '/admin/faq',
    CREATE_FAQ: '/admin/faq',
    UPDATE_FAQ: (id: string) => `/admin/faq/${id}`,
    DELETE_FAQ: (id: string) => `/admin/faq/${id}`,
    
    // Pages
    GET_PAGES: '/admin/pages',
    GET_PAGE: (slug: string) => `/admin/pages/${slug}`,
    CREATE_PAGE: '/admin/pages',
    UPDATE_PAGE: (id: string) => `/admin/pages/${id}`,
    DELETE_PAGE: (id: string) => `/admin/pages/${id}`,

    // Referrals
    GET_REFERRALS: '/admin/referrals',
    GET_REFERRAL_SETTINGS: '/admin/referrals/settings',
    UPDATE_REFERRAL_SETTINGS: '/admin/referrals/settings',

    // Wallet
    GET_WALLET_TRANSACTIONS: '/admin/wallet/transactions',
    ADJUST_WALLET: (userId: string) => `/admin/wallet/${userId}/adjust`,

    // Reactivation Requests
    GET_REACTIVATION_REQUESTS: '/admin/reactivation-requests',
    APPROVE_REACTIVATION: (id: string) => `/admin/users/${id}/reactivate`,
    REJECT_REACTIVATION: (id: string) => `/admin/users/${id}/reject-reactivation`,

    // Interactions
    GET_INTERACTIONS: '/admin/interactions',
    GET_INTERACTION_STATS: '/admin/interactions/stats',
  },

  // FCM (Firebase Cloud Messaging) Endpoints
  FCM: {
    REGISTER_TOKEN: '/fcm/register',
    UNREGISTER_TOKEN: '/fcm/unregister',
  },

  // Profile Analytics Endpoints
  PROFILE_ANALYTICS: {
    GET_MY_ANALYTICS: '/profile-analytics/me',
    GET_ANALYTICS: (id: string) => `/profile-analytics/${id}`,
  },

  // Profile View Endpoints
  PROFILE_VIEW: {
    RECORD_VIEW: (id: string) => `/profile-views/${id}`,
    GET_MY_VIEWS: '/profile-views/my-views',
    GET_WHO_VIEWED_ME: '/profile-views/who-viewed-me',
  },
} as const;

export default API_ENDPOINTS;